import { useTranslation } from 'react-i18next';
import React from 'react';
import { HiOutlineEllipsisHorizontal } from "react-icons/hi2";

/**
 * Pagination component for handling page navigation and page size changes.
 *
 * @param {Object} props - The properties object.
 * @param {number} props.page - The current page number.
 * @param {number} props.pageSize - The number of items per page.
 * @param {number} props.totalPageNum - The total number of pages.
 * @param {number} props.totalCount - The total number of items.
 * @param {function} props.onPageChange - Callback function to handle page change.
 * @param {function} props.onPageSizeChange - Callback function to handle page size change.
 * @returns {JSX.Element} The Pagination component.
 * 
 * expmple usage:
 * import Pagination from '../utils/Pagination'
 * const [page, setPage] = useState(1);
 * const [pageSize, setPageSize] = useState(10);
 * const customers = data?.allCustomer?.data || [];
 * const totalPageNum = data?.allCustomer?.totalPageNum || 1;
 * const totalCount = data?.allCustomer?.totalCount || 0;
 * 
   const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
    };

    <Pagination
        page={page}
        pageSize={pageSize}
        totalPageNum={totalPageNum}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
    />
 */
const Pagination = ({ page, pageSize, totalPageNum, totalCount, onPageChange, onPageSizeChange, containerClassName }) => {
    const { t } = useTranslation();
    const handleNextPage = () => {
        onPageChange(Math.min(page + 1, totalPageNum));
    };

    const handlePrevPage = () => {
        onPageChange(Math.max(page - 1, 1));
    };

    const handlePageSizeChange = (event) => {
        onPageSizeChange(Number(event.target.value));
        onPageChange(1); // Reset to first page
    };

    const handlePagination = (pageNum) => {
        onPageChange(pageNum);
    };

    return (
        (<div className={containerClassName || 'pagination-container'}>
            <div className='page-size-select'>{t('Show')} <select value={pageSize} onChange={handlePageSizeChange}>
                <option value="10">{t('10')}</option>
                <option value="25">{t('25')}</option>
                <option value="50">{t('50')}</option>
                <option value="100">{t('100')}</option>
            </select> {t('entries')}</div>
            <div className='flex items-center gap-[0.2rem] sm:gap-2'>
                <button onClick={() => handlePagination(1)}> {"<<"} </button>
                <button onClick={handlePrevPage} disabled={page === 1}>{'<'}</button>
                {page > 3 && (
                    <HiOutlineEllipsisHorizontal />
                )}
                {Array.from({ length: totalPageNum }, (_, i) => i + 1).slice(Math.max(page - 3, 0), Math.min(page + 2, totalPageNum)).map((pageNum) => (
                    <button key={pageNum} onClick={() => handlePagination(pageNum)} className={pageNum === page ? 'selected' : 'notSelected'}>
                        {pageNum}
                    </button>
                ))}
                {page <= (totalPageNum - 3) && (
                    <HiOutlineEllipsisHorizontal />
                )}
                <button onClick={handleNextPage} disabled={page >= totalPageNum}>{'>'}</button>
                <button onClick={() => handlePagination(totalPageNum)}> {">>"} </button>
            </div>
            <span className='displayTotalCount text-[#666666]'>
                {((page - 1) * pageSize) + 1} {t('to')} {Math.min(totalCount, page * pageSize)} {t('of')} {totalCount}
            </span>
        </div>)
    );
};

export default Pagination;